import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFileCsv } from '@fortawesome/pro-regular-svg-icons'
import toast from 'react-hot-toast'
import { ExportToCsv } from 'export-to-csv'

import analyticsService from '../services/analyticsService'
import placeService from '../services/placeService'
import ErrorPage from '../components/ErrorPage'
import AnalyticsFilter from '../components/Analytics/AnalyticsFilter'
import AnalyticsTable from '../components/Analytics/AnalyticsTable'
import { BuzzlUsersError } from '../types/buzzl-user'
import { useUser } from '../hooks/useUser'

const Analytics: React.FC = () => {
  const user = useUser()

  const [customerId, setCustomerId] = useState<string | undefined>(undefined)
  const [locationId, setLocationId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!user || !user.data) {
      return
    }

    if (user.data.type === 'CUSTOMER' && user.data.customerUser && user.data.customerUser.customer) {
      setCustomerId(user.data.customerUser.customer.id)
    } else {
      setCustomerId(undefined)
    }
  }, [user])

  const stats = useQuery(
    ['stats', customerId, locationId],
    () =>
      analyticsService.getAll({
        customerId,
        locationId,
      }),
    {
      onError: (e) => {
        const error = e as BuzzlUsersError
        error.response.errors.map((er) =>
          toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
        )
      },
      select: (data) => {
        //due to the API error if a location selected, placeCount is wrong. It should be
        if (locationId) {
          return {
            ...data,
            places: {
              count: 1,
            },
          }
        }

        return data
      },
    }
  )

  const places = useQuery(['places', 'analytics', customerId, locationId], () => {
    const where: { id?: string; customer?: { id: string } } = {}

    if (customerId) {
      where.customer = { id: customerId }
    }

    if (locationId) {
      where.id = locationId
    }

    if (customerId || locationId) {
      return placeService.getAllWithAnalytics({
        where,
      })
    }

    return placeService.getAllWithAnalytics()
  })

  if ((!stats.data && !stats.isLoading) || stats.error) {
    return (
      <ErrorPage
        title="Something went wrong!"
        text="We keep track of these errors, but feel free to contact us if refreshing doesn't fix things."
      />
    )
  }

  const statArray = [
    { title: 'Total Locations', value: stats.data?.places.count },
    { title: 'Total Experiences', value: stats.data?.experiences.count },
    {
      title: 'Total Positive Experiences',
      value: stats.data?.positiveExperiences.count,
    },
    {
      title: 'Total Negative Experiences',
      value: stats.data?.negativeExperiences.count,
    },
    {
      title: 'Total Neutral Experiences',
      value: stats.data?.neutralExperiences.count,
    },
    {
      title: 'Total At Location Experiences',
      value: stats.data?.verifiedExperiences.count,
    },
    {
      title: 'Total Verified Experiences',
      value: stats.data?.validatedExperiences.count,
    },
    {
      title: 'Total Offers Redeemed',
      value: stats.data?.redeemedOffers.count,
    },
    {
      title: 'Total Rewards Redeemed',
      value: stats.data?.redeemedRewards.count,
    },
    {
      title: 'Total Experiences Shared',
      value: stats.data?.experienceShared.count,
    },
    {
      title: 'Total Places Viewed',
      value: stats.data?.placeViewed.count,
    },
    {
      title: 'Total Place Buttons Clicked',
      value: stats.data?.placeBtnClicked.count,
    },
  ]

  const statToExport = places.data
    ? places.data.map((place) => ({
        customer: place.customer ? place.customer.name : '',
        location: place.name,
        views: place.analytics.views,
        actions: place.analytics.actions,
        nbExperiences: place.nbExperiences,
        nbPositiveExperiences: place.analytics.nbPositiveExperiences,
        nbNeutralExperiences: place.analytics.nbNeutralExperiences,
        nbNegativeExperiences: place.analytics.nbNegativeExperiences,
        distinctExperiencesPercentage: place.analytics.distinctExperiencesPercentage,
      }))
    : []

  const handleExport = () => {
    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: `Buzzjar Analytics Export - ${new Date().toLocaleDateString('en-US')}`,
      filename: 'buzzjar-analytics-export',
      useTextFile: false,
      useBom: true,
      headers: [
        'Customer',
        'Location',
        'Views',
        'Actions',
        'Total Experiences',
        '# of Postive',
        '# of Neutral',
        '# of Negative',
        'Distinct Experience',
      ],
    }
    const csvExporter = new ExportToCsv(options)
    csvExporter.generateCsv(statToExport)
  }

  return (
    <div>
      <div className="mb-12 flex items-center justify-between">
        <h1 className="text-lg font-medium leading-6">Analytics</h1>
        <button className="btn btn-white" onClick={handleExport}>
          <FontAwesomeIcon className="leading-icon" icon={faFileCsv} />
          Export
        </button>
      </div>

      <AnalyticsFilter setCustomerId={setCustomerId} setLocationId={setLocationId} />

      {stats.isLoading ? (
        <div>
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array(8)
              .fill('')
              .map((_stat, index) => (
                <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6" key={index}>
                  <dt className="mt-2 truncate text-sm font-medium text-gray-500">
                    <p className="animated h-4 w-1/2 animate-pulse rounded bg-gray-100" />
                  </dt>
                  <dd className="mt-1 text-3xl font-semibold text-gray-900">
                    <p className="animated h-12 w-1/3 animate-pulse rounded bg-gray-200" />
                  </dd>
                </div>
              ))}
          </dl>
        </div>
      ) : (
        <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {statArray.map((stat) => (
            <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6" key={stat.title}>
              <dt className="truncate text-sm font-medium text-gray-500">{stat.title}</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{stat.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <AnalyticsTable data={places.data} isLoading={places.isLoading} />
    </div>
  )
}

export default Analytics
